import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Loader2, Layers, Users, Calendar, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const money = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n) : String(v);
};

const EMPTY_FORM = { name: "", partner: "", phase: "", budget_planned: "", start_date: "", end_date: "", description: "" };

function ProgramCard({ p, canEdit, onEdit, onDelete }) {
  return (
    <Card className="overflow-hidden" data-testid={`program-card-${p.id}`}>
      {p.image && (
        <img
          src={`${API}/api/programs/${p.id}/image`}
          alt={p.name}
          className="w-full h-32 object-contain bg-slate-50 dark:bg-slate-900 p-4"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      )}
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="font-semibold">{p.name}</p>
          <div className="flex items-center gap-1">
            {p.phase && <Badge variant="outline">{p.phase}</Badge>}
            {canEdit && (
              <>
                <button onClick={() => onEdit(p)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-program-${p.id}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                {!p.airtable_record_id && (
                  <button onClick={() => onDelete(p)} className="text-muted-foreground hover:text-red-600" data-testid={`delete-program-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </>
            )}
          </div>
        </div>
        {p.partner && <p className="text-sm text-muted-foreground">{p.partner}</p>}
        {p.description && <p className="text-xs text-muted-foreground line-clamp-3">{p.description}</p>}
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground pt-1">
          {money(p.budget_planned) && <span>Presupuesto: {money(p.budget_planned)}</span>}
          {p.start_date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{p.start_date}</span>}
          {(p.sponsors || []).length > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{p.sponsors.join(", ")}</span>}
        </div>
        {p.tracking_2025 && <p className="text-xs whitespace-pre-wrap border-t pt-2 mt-2">{p.tracking_2025}</p>}
        {(p.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-1 pt-1">
            {p.tags.map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ProgramasPage() {
  const { token, user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const canEdit = ["admin", "rd_lead", "open_innovation_lead"].includes(user?.role);

  const load = () => {
    setLoading(true);
    api.getPrograms(token).then((d) => setPrograms(d.items || [])).finally(() => setLoading(false));
  };

  useEffect(load, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", partner: p.partner || "", phase: p.phase || "",
      budget_planned: p.budget_planned || "", start_date: p.start_date || "", end_date: p.end_date || "",
      description: p.description || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.updateProgram(token, editing.id, form);
        toast.success("Programa actualizado");
      } else {
        await api.createProgram(token, form);
        toast.success("Programa creado");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar el programa");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el programa "${p.name}"?`)) return;
    try {
      await api.deleteProgram(token, p.id);
      toast.success("Programa eliminado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="programas-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><Layers className="w-7 h-7" />Programas</h1>
          <p className="text-muted-foreground mt-1">Programas de partnership e innovación abierta (South Summit, BIND, Shakers, Santander X...)</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="create-program-btn">
            <Plus className="w-4 h-4 mr-2" />Nuevo Programa
          </Button>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
      ) : programs.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="programas-empty">Sin programas importados todavía.</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="programas-grid">
          {programs.map((p) => <ProgramCard key={p.id} p={p} canEdit={canEdit} onEdit={openEdit} onDelete={remove} />)}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="program-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Programa" : "Nuevo Programa"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="program-name">Nombre *</Label>
              <Input id="program-name" data-testid="program-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="program-partner">Socio / Partner</Label>
              <Input id="program-partner" data-testid="program-partner-input" value={form.partner} onChange={(e) => setForm({ ...form, partner: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="program-phase">Fase</Label>
                <Input id="program-phase" data-testid="program-phase-input" value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="program-budget">Presupuesto</Label>
                <Input id="program-budget" type="number" data-testid="program-budget-input" value={form.budget_planned} onChange={(e) => setForm({ ...form, budget_planned: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="program-start">Inicio</Label>
                <Input id="program-start" type="date" data-testid="program-start-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="program-end">Fin</Label>
                <Input id="program-end" type="date" data-testid="program-end-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="program-description">Descripción</Label>
              <Textarea id="program-description" rows={3} data-testid="program-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="save-program-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
